/**
 * CR106: Global Theme Provider
 * CR109: Added font registry integration
 * 
 * Provides global design tokens to the entire app.
 * These tokens are loaded from app_config and apply across ALL deal types.
 * 
 * Theme Labs edits these tokens globally.
 * Deal Cards tab edits per-type templates (sections, order, labels).
 */

import { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  type GlobalThemeTokens,
  type ThemePresetKey,
  DEFAULT_GLOBAL_THEME,
  THEME_PRESETS,
  hslToCss,
  FONT_FAMILY_MAP,
  FONT_SCALE_MAP,
  LINE_HEIGHT_MAP,
  CARD_PADDING_MAP,
  SECTION_GAP_MAP,
  HEADER_HEIGHT_MAP,
  CARD_SHADOW_MAP,
  BADGE_HEIGHT_MAP,
  BADGE_RADIUS_MAP,
  BUTTON_RADIUS_MAP,
  STARBURST_SIZE_MAP,
} from "@/lib/deals/globalThemeTokens";
import { resolveFontFamily, DEFAULT_BODY_FONT_ID, DEFAULT_HEADING_FONT_ID } from "@/lib/theme/fontRegistry";

// ================================================================
// COMPUTED STYLE HELPERS
// ================================================================

export interface ComputedThemeStyles {
  // Typography
  headingFontFamily: string;
  bodyFontFamily: string;
  fontScale: number;
  lineHeight: number;
  
  // Colors (CSS values)
  cardBackground: string;
  cardBorder: string;
  headerBackground: string;
  headerText: string;
  bodyText: string;
  mutedText: string;
  accentColor: string;
  
  // Spacing
  cardPadding: string;
  sectionGap: string;
  headerHeight: string;
  
  // Component styles
  cardCornerRadius: string;
  cardShadow: string;
  cardBorderWidth: string;
  badgeHeight: string;
  badgeRadius: string;
  badgeBorderWidth: string;
  buttonRadius: string;
  buttonSize: string;
  starburstSize: { width: string; height: string };
  starburstOffsetX: string;
  starburstOffsetY: string;
}

function computeStyles(tokens: GlobalThemeTokens): ComputedThemeStyles {
  // CR109: Prefer fontId from registry, fallback to legacy headingFont/bodyFont
  const headingFontFamily = tokens.typography.headingFontId
    ? resolveFontFamily(tokens.typography.headingFontId, DEFAULT_HEADING_FONT_ID)
    : FONT_FAMILY_MAP[tokens.typography.headingFont];
  const bodyFontFamily = tokens.typography.bodyFontId
    ? resolveFontFamily(tokens.typography.bodyFontId, DEFAULT_BODY_FONT_ID)
    : FONT_FAMILY_MAP[tokens.typography.bodyFont];

  return {
    // Typography
    headingFontFamily,
    bodyFontFamily,
    fontScale: FONT_SCALE_MAP[tokens.typography.fontScale],
    lineHeight: LINE_HEIGHT_MAP[tokens.typography.lineHeight],
    
    // Colors
    cardBackground: hslToCss(tokens.colors.cardBackground),
    cardBorder: hslToCss(tokens.colors.cardBorder),
    headerBackground: hslToCss(tokens.colors.headerBackground),
    headerText: hslToCss(tokens.colors.headerText),
    bodyText: hslToCss(tokens.colors.bodyText),
    mutedText: hslToCss(tokens.colors.mutedText),
    accentColor: hslToCss(tokens.colors.accentColor),
    
    // Spacing
    cardPadding: CARD_PADDING_MAP[tokens.spacing.cardPadding],
    sectionGap: SECTION_GAP_MAP[tokens.spacing.sectionGap],
    headerHeight: HEADER_HEIGHT_MAP[tokens.spacing.headerHeight],
    
    // Component styles
    cardCornerRadius: `${tokens.components.cardCornerRadius}px`,
    cardShadow: CARD_SHADOW_MAP[tokens.components.cardShadow],
    cardBorderWidth: `${tokens.components.cardBorderWidth}px`,
    badgeHeight: BADGE_HEIGHT_MAP[tokens.components.badge.height],
    badgeRadius: BADGE_RADIUS_MAP[tokens.components.badge.borderRadius],
    badgeBorderWidth: `${tokens.components.badge.borderWidth}px`,
    buttonRadius: BUTTON_RADIUS_MAP[tokens.components.button.borderRadius],
    buttonSize: tokens.components.button.size,
    starburstSize: STARBURST_SIZE_MAP[tokens.components.starburst.size],
    starburstOffsetX: `${tokens.components.starburst.offsetX}px`,
    starburstOffsetY: `${tokens.components.starburst.offsetY}px`,
  };
}

// ================================================================
// CONTEXT
// ================================================================

interface GlobalThemeContextValue {
  tokens: GlobalThemeTokens;
  preset: ThemePresetKey;
  styles: ComputedThemeStyles;
  loading: boolean;
  refetch: () => Promise<void>;
}

const GlobalThemeContext = createContext<GlobalThemeContextValue | null>(null);

// ================================================================
// PROVIDER
// ================================================================

interface GlobalThemeProviderProps {
  children: ReactNode;
  /** Optional override tokens for preview mode (Theme Labs) */
  overrideTokens?: GlobalThemeTokens | null;
  /** Optional override preset for preview mode */
  overridePreset?: ThemePresetKey | null;
}

export function GlobalThemeProvider({ 
  children, 
  overrideTokens, 
  overridePreset 
}: GlobalThemeProviderProps) {
  const [dbTokens, setDbTokens] = useState<GlobalThemeTokens>(DEFAULT_GLOBAL_THEME);
  const [dbPreset, setDbPreset] = useState<ThemePresetKey>("light");
  const [loading, setLoading] = useState(true);

  const fetchTokens = useCallback(async () => {
    try {
      // Load global theme tokens
      const { data: configData } = await supabase
        .from("app_config")
        .select("*")
        .eq("key", "global_theme_tokens")
        .single();

      if (configData?.value_json) {
        const tokens = configData.value_json as unknown as GlobalThemeTokens;
        setDbTokens({ ...DEFAULT_GLOBAL_THEME, ...tokens });
      }

      // Load active preset
      const { data: presetData } = await supabase
        .from("app_config")
        .select("*")
        .eq("key", "global_theme_preset")
        .single();

      if (presetData?.value_json) {
        const preset = (presetData.value_json as { preset: ThemePresetKey }).preset;
        setDbPreset(preset || "light");
      }
    } catch (err) {
      console.error("[GlobalThemeProvider] Load error:", err);
      // Non-fatal - use defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  // Use override tokens if provided (for Theme Labs preview), otherwise use DB tokens
  const effectiveTokens = useMemo(() => {
    return overrideTokens ?? dbTokens;
  }, [overrideTokens, dbTokens]);

  const effectivePreset = useMemo(() => {
    return overridePreset ?? dbPreset;
  }, [overridePreset, dbPreset]);

  // Compute CSS-ready styles from tokens
  const styles = useMemo(() => {
    return computeStyles(effectiveTokens);
  }, [effectiveTokens]);

  const value = useMemo<GlobalThemeContextValue>(() => ({
    tokens: effectiveTokens,
    preset: effectivePreset,
    styles,
    loading,
    refetch: fetchTokens,
  }), [effectiveTokens, effectivePreset, styles, loading, fetchTokens]);

  return (
    <GlobalThemeContext.Provider value={value}>
      {children}
    </GlobalThemeContext.Provider>
  );
}

// ================================================================
// HOOK
// ================================================================

export function useGlobalTheme(): GlobalThemeContextValue {
  const context = useContext(GlobalThemeContext);
  if (!context) {
    // Return default values if not wrapped in provider (fallback)
    return {
      tokens: DEFAULT_GLOBAL_THEME,
      preset: "light",
      styles: computeStyles(DEFAULT_GLOBAL_THEME),
      loading: false,
      refetch: async () => {},
    };
  }
  return context;
}

// ================================================================
// STANDALONE HELPERS (for non-React contexts)
// ================================================================

let cachedTokens: GlobalThemeTokens = DEFAULT_GLOBAL_THEME;
let cachedStyles: ComputedThemeStyles = computeStyles(DEFAULT_GLOBAL_THEME);

export async function loadGlobalThemeCache(): Promise<void> {
  try {
    const { data } = await supabase
      .from("app_config")
      .select("*")
      .eq("key", "global_theme_tokens")
      .single();

    if (data?.value_json) {
      cachedTokens = { ...DEFAULT_GLOBAL_THEME, ...(data.value_json as unknown as GlobalThemeTokens) };
      cachedStyles = computeStyles(cachedTokens);
    }
  } catch (err) {
    console.error("[loadGlobalThemeCache] Error:", err);
  }
}

export function getCachedGlobalTokens(): GlobalThemeTokens {
  return cachedTokens;
}

export function getCachedGlobalStyles(): ComputedThemeStyles {
  return cachedStyles;
}
